import { useState, useEffect } from "react";
import { X, Download, Smartphone, Copy, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { toast } from "sonner";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

// Detect if user is on iOS but NOT using Safari
const isIOSNonSafari = () => {
  const ua = navigator.userAgent;
  const isIOS = /iPhone|iPad|iPod/.test(ua);
  // Safari on iOS has "Safari" in UA but NOT "CriOS" (Chrome), "FxiOS" (Firefox), "EdgiOS" (Edge)
  const isNotSafari = /CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
  return isIOS && isNotSafari;
};

export const PWAInstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isNonSafariIOS, setIsNonSafariIOS] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia("(display-mode: standalone)").matches) {
      return;
    }

    // Check if user dismissed the prompt before
    const dismissed = localStorage.getItem("pwa-prompt-dismissed");
    if (dismissed) {
      const dismissedTime = parseInt(dismissed, 10);
      // Show again after 7 days
      if (Date.now() - dismissedTime < 7 * 24 * 60 * 60 * 1000) {
        return;
      }
    }

    // Check for iOS non-Safari browser
    if (isIOSNonSafari()) {
      setIsNonSafariIOS(true);
      const timer = setTimeout(() => {
        setShowPrompt(true);
      }, 3000);
      return () => clearTimeout(timer);
    }

    // Show prompt after a short delay for mobile users
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (isMobile) {
      const timer = setTimeout(() => {
        setShowPrompt(true);
      }, 5000);

      return () => clearTimeout(timer);
    }

    // Listen for install prompt on Android/Desktop
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setShowPrompt(true);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    };
  }, []);

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem("pwa-prompt-dismissed", Date.now().toString());
  };

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") {
        setShowPrompt(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.origin);
      toast.success("Link copiado! Agora abra o Safari e cole na barra de endereço.");
    } catch {
      toast.error("Não foi possível copiar o link");
    }
  };

  if (!showPrompt) return null;

  // Special prompt for iOS users not on Safari
  if (isNonSafariIOS) {
    return (
      <div className="fixed bottom-4 left-4 right-4 z-50 md:left-auto md:right-4 md:max-w-sm animate-in slide-in-from-bottom-4">
        <div className="bg-card border border-border rounded-lg shadow-lg p-4">
          <button
            onClick={handleDismiss}
            className="absolute top-2 right-2 text-muted-foreground hover:text-foreground"
            aria-label="Fechar"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center">
              <ExternalLink className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-display text-sm font-medium mb-1">Abra no Safari</h3>
              <p className="text-xs text-muted-foreground mb-3">
                Para instalar o app no iPhone, é necessário usar o Safari. Copie o link e abra no Safari.
              </p>

              <div className="flex gap-2">
                <Button size="sm" onClick={handleCopyLink} className="flex-1">
                  <Copy className="w-4 h-4 mr-1" />
                  Copiar Link
                </Button>
                <Button size="sm" variant="ghost" onClick={handleDismiss}>
                  Depois
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 md:left-auto md:right-4 md:max-w-sm animate-in slide-in-from-bottom-4">
      <div className="bg-card border border-border rounded-lg shadow-lg p-4">
        <button
          onClick={handleDismiss}
          className="absolute top-2 right-2 text-muted-foreground hover:text-foreground"
          aria-label="Fechar"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-12 h-12 bg-logo-brown-dark rounded-lg flex items-center justify-center">
            <Smartphone className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-display text-sm font-medium mb-1">Instale o App TUPJA</h3>
            <p className="text-xs text-muted-foreground mb-3">
              Acesse o terreiro direto da sua tela inicial
            </p>

            <div className="flex gap-2">
              {deferredPrompt ? (
                <Button size="sm" onClick={handleInstall} className="flex-1">
                  <Download className="w-4 h-4 mr-1" />
                  Instalar
                </Button>
              ) : (
                <Button size="sm" asChild className="flex-1" onClick={handleDismiss}>
                  <Link to="/instalar">Ver como instalar</Link>
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={handleDismiss}>
                Agora não
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
